<template>
  <div id="app">
    <router-view />

    <portal-target name="destination">
      <!--
  This component can be located anywhere in your App.
  The slot content of the above portal component will be rendered here.
  -->
    </portal-target>

    <div class="ai-chat" @mousedown="startDrag" v-if="hasAgent && aiAssistantEnabled">
      <div class="avatar-wrapper">
        <el-popover :key="popoverKey" ref="dcPopover" visible-arrow="false" trigger="manual"
          transition="el-zoom-in-bottom"
          :popper-class="isFullscreen ? 'ai-popover ai-popover-fullscreen' : 'ai-popover'" v-model="popoverVisible"
          :width="isFullscreen ? null : 500" placement="top-end">
          <img slot="reference" src="@/assets/images/ai-logo.gif" class="ai-icon" v-show="!isFullscreen" />
          <template #default>
            <div class="mode" :class="isFullscreen ? 'fullMode' : '.mode'">
              <span class="modeItems" @click="toggleFullscreen">
                <span :class="isFullscreen ? 'el-icon-copy-document' : 'el-icon-full-screen'"></span>
              </span>
              <span class="modeItems" @click="closeView">
                <span class="el-icon-close"></span>
              </span>
            </div>
            <div @mousedown.stop>
              <dialogChat v-if="popoverVisible" :accessToken="accessToken" :isFullscreen="isFullscreen"></dialogChat>
            </div>
          </template>
        </el-popover>
      </div>
    </div>
  </div>
</template>

<script>
import websocket from "./utils/websocket/index";
import { startCertifiesTask } from "@/api/login/login.js";
import { getAiList } from "@/api/AI/index.js";
import dialogChat from "@/components/AI/dialogChat.vue";
import Vue from "vue";

import { getToken } from "@/utils/auth"
export default {
  name: "App",
  components: {
    dialogChat
  },
  data() {
    return {
      hasAgent: true,      // 控制AI图标是否显示
      popoverVisible: false, // 控制弹框是否显示
      isFullscreen: false, // 控制AI窗口是否全屏
      popoverKey: 0, // 用于强制重新渲染 popover
      accessToken: null,
      clickTimeout: null,
      fixedWidth: 100,
      fixedHeight: 110,
      firstTime: null,
      lastTime: null,
      isDragging: false,
      hasMoved: false,
      offset: { x: 0, y: 0 }
    };
  },
  computed: {
    aiAssistantEnabled() {
      return this.$store.state.settings.aiAssistant;
    }
  },
  watch: {
    $route: {
      handler() {
        const token = getToken();
        if (token) {
          this.getAgentList();
        } else {
          this.hasAgent = false;
          this.accessToken = null;
        }
      },
      immediate: true
    }
  },
  created() {
    // 初始化主题设置 - 确保刷新页面后暗黑模式能正确应用
    this.initTheme();

    this.$nextTick(() => {
      const wsInstance = websocket.Instance;
      wsInstance.newvue = this;
      Vue.prototype.$ws = wsInstance;
      const user = JSON.parse(localStorage.getItem("user"));
      if (user?.id) {
        wsInstance.connect(user.id);
      }
    });

    this.starTask();
    this.getAgentList();
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('mousemove', this.handleMouseMove);
      window.addEventListener('mouseup', this.stopDrag);
      document.addEventListener('click', this.handleClickOutside);
    });
  },
  beforeDestroy() {
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.stopDrag);
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    closeView() {
      const wasFullscreen = this.isFullscreen;
      this.popoverVisible = false;
      this.isFullscreen = false;
      // 如果之前是全屏模式，增加 key 强制重建 popover
      if (wasFullscreen) {
        this.popoverKey++;
      }
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
    },
    handleClickOutside(event) {
      if (!this.popoverVisible) return;
      const popover = document.querySelector('.ai-popover');
      const aiChat = document.querySelector('.ai-chat');
      if (popover && !popover.contains(event.target) && aiChat && !aiChat.contains(event.target)) {
        const wasFullscreen = this.isFullscreen;
        this.isFullscreen = false;
        this.popoverVisible = false;
        // 如果之前是全屏模式，增加 key 强制重建 popover
        if (wasFullscreen) {
          this.popoverKey++;
        }
      }
    },
    startDrag(event) {
      event.preventDefault(); // 阻止默认拖拽行为
      this.firstTime = Date.now();
      this.isDragging = true;
      this.hasMoved = false;
      
      const aiChatElement = event.currentTarget;
      const rect = aiChatElement.getBoundingClientRect();
      
      // 首次拖动时，将 right/bottom 定位转换为 left/top
      if (!aiChatElement.style.left) {
        aiChatElement.style.left = `${rect.left}px`;
        aiChatElement.style.top = `${rect.top}px`;
        aiChatElement.style.right = 'auto';
        aiChatElement.style.bottom = 'auto';
      }
      
      this.offset.x = event.clientX - rect.left;
      this.offset.y = event.clientY - rect.top;
    },
    stopDrag() {
      if (!this.isDragging) return;

      const duration = Date.now() - this.firstTime;
      this.isDragging = false;

      // 点击时间小于200ms且没有移动，视为点击事件，触发弹框切换
      if (duration < 200 && !this.hasMoved) {
        if (!this.popoverVisible) {
          this.isFullscreen = false;
          this.popoverVisible = true;
        } else {
          this.popoverVisible = false;
        }
      }
    },
    handleMouseMove(event) {
      if (!this.isDragging) return;

      event.preventDefault(); // 阻止选中文本等默认行为
      this.hasMoved = true;
      const aiChatElement = document.querySelector('.ai-chat');
      if (!aiChatElement) return;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let newX = event.clientX - this.offset.x;
      let newY = event.clientY - this.offset.y;

      // 边界限制
      newX = Math.max(0, Math.min(newX, viewportWidth - this.fixedWidth));
      newY = Math.max(0, Math.min(newY, viewportHeight - this.fixedHeight));

      aiChatElement.style.left = `${newX}px`;
      aiChatElement.style.top = `${newY}px`;
    },
    async getAgentList() {
      return;//2.0记得去除
      try {
        const res = await getAiList({ pageNum: 1, pageSize: 9999, type: 0 });
        
        if (res.code === 200) {
          this.accessToken = res.rows.find((v) => v.showWindows)?.accessToken;
          this.hasAgent = !!this.accessToken;
          // 存储到 Vuex，供 AIHome 组件使用
          this.$store.dispatch('settings/changeSetting', { key: 'aiAccessToken', value: this.accessToken });
        } else {
          this.accessToken = null;
          this.hasAgent = false;
          this.$store.dispatch('settings/changeSetting', { key: 'aiAccessToken', value: null });
        }
      } catch (error) {
        console.error('获取AI应用列表失败:', error);
        this.accessToken = null;
        this.hasAgent = false;
        this.$store.dispatch('settings/changeSetting', { key: 'aiAccessToken', value: null });
      }
    },
    async starTask() {
      try {
        const res = await startCertifiesTask();
        if (res.code === 200) {
          console.log("证照预警定时任务启动成功");
        }
      } catch (error) {
        console.error("证照预警定时任务启动失败:", error);
      }
    },
    // 初始化主题设置
    initTheme() {
      const skin = this.$store.state.settings.Skin;
      if (skin === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  }
};
</script>

<style>
@import "@/styles/index.css";
@import "@/styles/date-style.less";

body {
  margin: 0;
  max-width: 100% !important;
  max-height: 100% !important;
  font-size: 12px !important;
  background: var(--background);
  color: var(--textColor);
}

#app {
  position: absolute;
  /* 使用绝对定位 */
  top: 0;
  left: 0;
  width: 100%;
  /* 设置宽度为父元素（body）的宽度 */
  height: 100%;
  /* 设置高度为父元素（body）的高度 */
}

/* 更改进度条颜色 */
#nprogress .bar {
  background: var(--background-4) !important;
  height: 2px !important;
}

/* 更改 avnt X6 边线条颜色 */
@keyframes ant-line {
  to {
    stroke-dashoffset: -1000;
  }
}

.ai-chat {
  width: 80px;
  height: 80px;
  position: absolute;
  right: 1%;
  bottom: 2%;
  cursor: move;
  /* 指示可拖动 */
  z-index: 10;
}

.ai-icon {
  width: 80px;
  height: 80px;
  cursor: pointer;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: auto;
}

.mode {
  width: 100%;
  height: 30px;
  position: absolute;
  top: -35px;
}

.fullMode {
  position: relative;
  top: 0px;
}

.mode {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 5px;
}

.active {
  color: var(--textColor);
  background: var(--background-2);
}

.modeItems {
  background: var(--background);
  cursor: pointer;
  border: 1px solid var(--border);
  border-radius: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 28px;
  margin-right: 10px;
  padding: 0 7px;
}
</style>

<style>
/* 全屏模式样式 - 需要全局样式覆盖 popover */
.ai-popover-fullscreen {
  position: fixed !important;
  top: 0 !important;
  right: 0 !important;
  left: auto !important;
  bottom: 0 !important;
  width: 50vw !important;
  height: 100vh !important;
  max-width: none !important;
  transform: none !important;
  border-radius: 0 !important;
  display: flex;
  flex-direction: column;
}

.ai-popover-fullscreen .el-popover__title {
  margin: 0;
}

/* 非全屏模式确保位置正常 */
.ai-popover:not(.ai-popover-fullscreen) {
  right: auto !important;
}
</style>
